import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import {
  Calendar,
  Clock,
  X,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Play,
  RotateCcw,
  Loader2,
  Users,
  UserPlus,
  ShieldCheck,
  ListOrdered,
  PlusCircle,
  Smartphone,
  CalendarCheck,
  Info,
  Repeat,
  Zap,
  Pencil,
  Trash2,
  User,
  Search,
  Eye,
  EyeOff,
  Sparkles,
  Library,
} from 'lucide-react';
import toast from 'react-hot-toast';
import playerProfileService from '../../apis/playerProfileService';
import SmsMessageOptimizerModal from '../sms/SmsMessageOptimizerModal';
import SmsPresetsLibraryModal from '../sms/SmsPresetsLibraryModal';

const CST_TIMEZONE = 'America/Chicago';

export default function ScheduledSmsModal({
  isOpen,
  onClose,
  initialMessage = '',
  targetType: initialTargetType = 'all_active',
  selectedRecipients = [],
  onOpenMessaging = null,
  activePlatform = 'twilio',
  onSwitchPlatform = null,
  gatewayBalance = null,
}) {
  // If opened with an initial message, default to the 'create' tab, otherwise start on 'manage'
  const [activeTab, setActiveTab] = useState(initialMessage ? 'create' : 'manage');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Edit Campaign State
  const [editingSchedule, setEditingSchedule] = useState(null);

  // Action Confirmation Modals State
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null);
  const [cancelConfirmTarget, setCancelConfirmTarget] = useState(null);
  const [testRunConfirmTarget, setTestRunConfirmTarget] = useState(null);
  const [showOptimizerModal, setShowOptimizerModal] = useState(false);
  const [showPresetsModal, setShowPresetsModal] = useState(false);

  // Form State
  const [campaignName, setCampaignName] = useState('');
  const [message, setMessage] = useState(initialMessage || '');
  const [targetType, setTargetType] = useState(initialTargetType);
  const [manualPhoneInput, setManualPhoneInput] = useState('');

  // Default to 1 hour from now in CST
  const [scheduleDate, setScheduleDate] = useState(
    moment().tz(CST_TIMEZONE).format('YYYY-MM-DD')
  );
  const [scheduleTime, setScheduleTime] = useState(
    moment().tz(CST_TIMEZONE).add(60, 'minutes').format('HH:mm')
  );

  // Recurrence / Repeating State
  const [isRecurring, setIsRecurring] = useState(false);
  const [repeatFrequency, setRepeatFrequency] = useState('once'); // 'once' | 'daily' | 'weekdays' | 'weekends'

  // Scheduled List State
  const [schedules, setSchedules] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [systemStatus, setSystemStatus] = useState(null);

  // Active Players Roster Viewer
  const [activePlayers, setActivePlayers] = useState([]);
  const [activePlayersLoading, setActivePlayersLoading] = useState(false);
  const [showActivePlayersList, setShowActivePlayersList] = useState(false);
  const [activeSearchTerm, setActiveSearchTerm] = useState('');

  const loadActivePlayers = useCallback(async () => {
    setActivePlayersLoading(true);
    try {
      const res = await playerProfileService.getActivePlayersPhones();
      const list = res?.data?.data?.players || [];
      setActivePlayers(list);
    } catch (e) {
      console.error('Failed to load active players in ScheduledSmsModal:', e);
    } finally {
      setActivePlayersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadActivePlayers();
    }
  }, [isOpen, loadActivePlayers]);

  const filteredActivePlayers = useMemo(() => {
    if (!activeSearchTerm.trim()) return activePlayers;
    const term = activeSearchTerm.toLowerCase().trim();
    return activePlayers.filter((p) => {
      const name = String(p.player_name || '').toLowerCase();
      const num = String(p.player_number || '').toLowerCase();
      const ph = String(p.phone || '').replace(/\D/g, '');
      return name.includes(term) || num.includes(term) || ph.includes(term);
    });
  }, [activePlayers, activeSearchTerm]);

  // Live real-time CST Clock that updates every second
  const [currentCst, setCurrentCst] = useState(() => moment().tz(CST_TIMEZONE));

  useEffect(() => {
    if (!isOpen) return;
    setCurrentCst(moment().tz(CST_TIMEZONE));
    const timer = setInterval(() => {
      setCurrentCst(moment().tz(CST_TIMEZONE));
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);

  // Sync initial props
  useEffect(() => {
    if (initialMessage && !message) {
      setMessage(initialMessage);
      setActiveTab('create');
    }
  }, [initialMessage, message]);

  useEffect(() => {
    if (initialTargetType) {
      setTargetType(initialTargetType);
    }
  }, [initialTargetType]);

  // Load schedules and system status
  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await playerProfileService.getScheduledSms({
        status: statusFilter,
        limit: 50,
      });

      if (res?.success && res.data?.data) {
        setSchedules(res.data.data.schedules || []);
        if (res.data.data.systemStatus) {
          setSystemStatus(res.data.data.systemStatus);
        }
      }
    } catch (e) {
      console.error('Failed to load scheduled SMS:', e);
      toast.error('Failed to load scheduled messages');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (isOpen) {
      fetchSchedules();
    }
  }, [isOpen, fetchSchedules]);

  // Calculate schedule moment and relative human status
  const computedSchedule = useMemo(() => {
    if (!scheduleDate || !scheduleTime) return null;
    const cstDateTimeString = `${scheduleDate} ${scheduleTime}`;
    const m = moment.tz(cstDateTimeString, 'YYYY-MM-DD HH:mm', CST_TIMEZONE);
    if (!m.isValid()) return null;

    const now = currentCst;
    const isPast = m.isBefore(now);
    const diffHours = m.diff(now, 'hours');
    const fromNowText = m.from(now);

    return {
      moment: m,
      formatted: m.format('dddd, MMMM D, YYYY [at] h:mm A'),
      timeOnly: m.format('h:mm A'),
      isPast,
      diffHours,
      fromNowText,
    };
  }, [scheduleDate, scheduleTime, currentCst]);

  // Quick Preset Handlers (Recurring Daily & Fast One-Time)
  const handleApplyPreset = (presetType) => {
    const now = moment().tz(CST_TIMEZONE);
    let targetMoment = now.clone();
    let recurring = false;
    let frequency = 'once';
    let defaultCampaign = '';

    switch (presetType) {
      case 'everyday_10am': {
        const today10 = now.clone().hour(10).minute(0).second(0);
        targetMoment = today10.isAfter(now) ? today10 : now.clone().add(1, 'day').hour(10).minute(0).second(0);
        recurring = true;
        frequency = 'daily';
        defaultCampaign = 'Daily 10:00 AM Announcement';
        break;
      }
      case 'everyday_3pm': {
        const today3 = now.clone().hour(15).minute(0).second(0);
        targetMoment = today3.isAfter(now) ? today3 : now.clone().add(1, 'day').hour(15).minute(0).second(0);
        recurring = true;
        frequency = 'daily';
        defaultCampaign = 'Daily 3:00 PM Announcement';
        break;
      }
      case 'everyday_7pm': {
        const today7 = now.clone().hour(19).minute(0).second(0);
        targetMoment = today7.isAfter(now) ? today7 : now.clone().add(1, 'day').hour(19).minute(0).second(0);
        recurring = true;
        frequency = 'daily';
        defaultCampaign = 'Daily 7:00 PM Announcement';
        break;
      }
      case 'next_10am': {
        const today10 = now.clone().hour(10).minute(0).second(0);
        targetMoment = today10.isAfter(now) ? today10 : now.clone().add(1, 'day').hour(10).minute(0).second(0);
        recurring = false;
        frequency = 'once';
        break;
      }
      case 'next_3pm': {
        const today3 = now.clone().hour(15).minute(0).second(0);
        targetMoment = today3.isAfter(now) ? today3 : now.clone().add(1, 'day').hour(15).minute(0).second(0);
        recurring = false;
        frequency = 'once';
        break;
      }
      case 'in_1_hour':
        targetMoment = now.clone().add(1, 'hour');
        recurring = false;
        frequency = 'once';
        break;
      case 'tonight_6pm': {
        const tonight = now.clone().hour(18).minute(0).second(0);
        targetMoment = tonight.isAfter(now) ? tonight : now.clone().add(1, 'day').hour(18).minute(0).second(0);
        recurring = false;
        frequency = 'once';
        break;
      }
      case 'tomorrow_11am':
        targetMoment = now.clone().add(1, 'day').hour(11).minute(0).second(0);
        recurring = false;
        frequency = 'once';
        break;
      case 'tomorrow_6pm':
        targetMoment = now.clone().add(1, 'day').hour(18).minute(0).second(0);
        recurring = false;
        frequency = 'once';
        break;
      case 'saturday_12pm': {
        let sat = now.clone();
        while (sat.day() !== 6 || sat.isSameOrBefore(now)) {
          sat.add(1, 'day');
        }
        targetMoment = sat.hour(12).minute(0).second(0);
        recurring = false;
        frequency = 'once';
        break;
      }
      default:
        break;
    }

    setScheduleDate(targetMoment.format('YYYY-MM-DD'));
    setScheduleTime(targetMoment.format('HH:mm'));
    setIsRecurring(recurring);
    setRepeatFrequency(frequency);
    if (defaultCampaign && !campaignName.trim()) {
      setCampaignName(defaultCampaign);
    }
  };

  if (!isOpen) return null;

  // Handle Form Submission
  const handleCreateSchedule = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      toast.error('Please enter an SMS message body');
      return;
    }

    if (!computedSchedule || !computedSchedule.moment.isValid()) {
      toast.error('Please choose a valid schedule date and time');
      return;
    }

    if (computedSchedule.isPast) {
      toast.error('The selected time is in the past! Please choose a future Central Time.');
      return;
    }

    // Parse any manual phone numbers
    const parsedManual = (manualPhoneInput || '')
      .split(/[\s,;]+/)
      .map((p) => p.replace(/\D/g, ''))
      .filter((p) => p.length >= 10)
      .map((p) => ({
        phone: p.startsWith('1') && p.length === 11 ? `+${p}` : p.length === 10 ? `+1${p}` : `+${p}`,
        player_name: 'Manual Recipient',
      }));

    const finalCustomRecipients = [...selectedRecipients, ...parsedManual];

    if (targetType === 'custom' && finalCustomRecipients.length === 0) {
      toast.error('Please select players from the directory or enter at least one phone number');
      return;
    }

    if (targetType === 'active_and_custom' && finalCustomRecipients.length === 0) {
      toast.error('Please enter at least one additional phone number or select players');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        campaign_name: campaignName.trim() || null,
        message: message.trim(),
        target_type: targetType,
        scheduled_time: computedSchedule.moment.toISOString(),
        timezone: CST_TIMEZONE,
        is_recurring: isRecurring,
        repeat_frequency: repeatFrequency,
        cron_expression: isRecurring ? repeatFrequency : null,
        recipients: (targetType === 'custom' || targetType === 'active_and_custom') ? finalCustomRecipients : [],
      };

      let res;
      if (editingSchedule) {
        res = await playerProfileService.updateScheduledSms(editingSchedule.id, payload);
      } else {
        res = await playerProfileService.createScheduledSms(payload);
      }

      if (res?.success) {
        toast.success(
          editingSchedule
            ? 'Scheduled campaign updated successfully'
            : isRecurring
              ? `Recurring schedule confirmed for ${repeatFrequency === 'daily' ? 'Every Day' : repeatFrequency} at ${computedSchedule.timeOnly} CST`
              : `Schedule confirmed for ${computedSchedule.moment.format('MMM D, h:mm A')} CST`
        );
        handleCancelEdit();
        setActiveTab('manage');
        fetchSchedules();
      } else {
        toast.error(res?.error || (editingSchedule ? 'Failed to update schedule' : 'Failed to create schedule'));
      }
    } catch (err) {
      console.error('Save schedule error:', err);
      toast.error(err.message || 'An error occurred saving schedule');
    } finally {
      setSubmitting(false);
    }
  };

  // Edit Schedule Handlers
  const handleStartEdit = (row) => {
    setEditingSchedule(row);
    setCampaignName(row.campaign_name || '');
    setMessage(row.message || '');
    setTargetType(row.target_type || 'all_active');

    const m = moment(row.scheduled_time).tz(CST_TIMEZONE);
    setScheduleDate(m.format('YYYY-MM-DD'));
    setScheduleTime(m.format('HH:mm'));

    setIsRecurring(Boolean(row.is_recurring));
    setRepeatFrequency(row.cron_expression || (row.is_recurring ? 'daily' : 'once'));

    if ((row.target_type === 'custom' || row.target_type === 'active_and_custom') && Array.isArray(row.recipients_snapshot)) {
      const phones = row.recipients_snapshot.map((r) => r.phone).filter(Boolean);
      setManualPhoneInput(phones.join(', '));
    } else {
      setManualPhoneInput('');
    }

    setActiveTab('create');
  };

  const handleCancelEdit = () => {
    setEditingSchedule(null);
    setCampaignName('');
    setMessage(initialMessage || '');
    setTargetType(initialTargetType);
    setManualPhoneInput('');
    setScheduleDate(moment().tz(CST_TIMEZONE).format('YYYY-MM-DD'));
    setScheduleTime(moment().tz(CST_TIMEZONE).add(60, 'minutes').format('HH:mm'));
    setIsRecurring(false);
    setRepeatFrequency('once');
  };

  // Handle Delete Schedule (triggers delete confirmation modal)
  const handleDeleteSchedule = (row) => {
    setDeleteConfirmTarget(row);
  };

  const confirmDeleteSchedule = async (id) => {
    if (!id) return;
    setActionLoadingId(id);
    try {
      const res = await playerProfileService.deleteScheduledSms(id);
      if (res?.success) {
        toast.success('Scheduled campaign deleted successfully');
        if (editingSchedule?.id === id) {
          handleCancelEdit();
        }
        setDeleteConfirmTarget(null);
        fetchSchedules();
      } else {
        toast.error(res?.error || 'Failed to delete schedule');
      }
    } catch (err) {
      console.error('Delete schedule error:', err);
      toast.error(err.message || 'Failed to delete schedule');
    } finally {
      setActionLoadingId(null);
    }
  };

  // Handle Cancel Schedule (triggers cancel confirmation modal)
  const handleCancelSchedule = (row) => {
    setCancelConfirmTarget(row);
  };

  const confirmCancelSchedule = async (id) => {
    if (!id) return;
    setActionLoadingId(id);
    try {
      const res = await playerProfileService.cancelScheduledSms(id);
      if (res?.success) {
        toast.success('Scheduled SMS cancelled');
        setCancelConfirmTarget(null);
        fetchSchedules();
      } else {
        toast.error(res?.error || 'Failed to cancel schedule');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to cancel schedule');
    } finally {
      setActionLoadingId(null);
    }
  };

  // Handle Test Run Immediately (triggers test run confirmation modal)
  const handleTestRunSchedule = (row) => {
    setTestRunConfirmTarget(row);
  };

  const confirmTestRunSchedule = async (id) => {
    if (!id) return;
    setActionLoadingId(id);
    try {
      const res = await playerProfileService.testRunScheduledSms(id);
      if (res?.success) {
        toast.success(res.message || 'Test run completed successfully');
        setTestRunConfirmTarget(null);
        fetchSchedules();
      } else {
        toast.error(res?.error || 'Test run failed');
      }
    } catch (err) {
      toast.error(err.message || 'Test run failed');
    } finally {
      setActionLoadingId(null);
    }
  };

  // Status Badge Helper
  const getStatusBadge = (status, isRecurring = false) => {
    switch (status) {
      case 'pending':
        if (isRecurring) {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
              <Repeat className="w-3 h-3 mr-1" />
              Active Recurring
            </span>
          );
        }
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Sending...
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
            <XCircle className="w-3 h-3 mr-1" />
            Cancelled
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  const charCount = message.length;
  const segments = Math.ceil(charCount / 160) || 1;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-2 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col border border-gray-100">

        {/* MODAL HEADER - BLACK, WHITE TEXT WITH YELLOW ACCENT */}
        <div className="px-6 py-4 bg-black text-white flex items-center justify-between border-b border-gray-800 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-400 rounded-xl shadow-md">
              <CalendarCheck className="h-6 w-6 text-gray-950" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold tracking-tight text-white">Scheduled SMS Campaigns</h2>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-400/20 text-amber-300 border border-amber-400/40">
                  Central Time (CST)
                </span>
                {onSwitchPlatform ? (
                  <select
                    value={activePlatform}
                    onChange={(e) => onSwitchPlatform(e.target.value)}
                    className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-900 text-amber-300 border border-amber-400/40 focus:outline-none cursor-pointer"
                    title="Switch SMS Gateway Platform"
                  >
                    <option value="twilio">Gateway: Twilio (Default)</option>
                    <option value="telnyx">Gateway: Telnyx</option>
                  </select>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-900 text-amber-300 border border-amber-400/40 capitalize">
                    Gateway: {activePlatform || 'Twilio'}
                  </span>
                )}
                {gatewayBalance && gatewayBalance.balance != null && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-bold border flex items-center gap-1 ${
                      gatewayBalance.balance < 5
                        ? 'bg-amber-500/20 text-amber-300 border-amber-400/50'
                        : 'bg-emerald-500/20 text-emerald-300 border-emerald-400/50'
                    }`}
                    title={`${activePlatform.toUpperCase()} Balance: $${Number(gatewayBalance.balance).toFixed(2)} ${gatewayBalance.currency || 'USD'}${
                      gatewayBalance.availableCredit != null ? ` (Available credit: $${Number(gatewayBalance.availableCredit).toFixed(2)})` : ''
                    }`}
                  >
                    💰 ${Number(gatewayBalance.balance).toFixed(2)} {gatewayBalance.currency || 'USD'}
                    {gatewayBalance.isMock && <span className="text-[9px] opacity-75 font-normal">(mock)</span>}
                    {gatewayBalance.balance < 5 && <span className="text-[9px] px-1 bg-amber-400 text-gray-950 rounded font-black">LOW</span>}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-300 mt-0.5">
                Automate promotional announcements & club notifications to send at an exact date and time
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-amber-400 p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            title="Close"
          >
            <X size={22} />
          </button>
        </div>

        {/* TIMEZONE SAFETY BANNER */}
        {systemStatus?.isLocalDev && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 font-semibold text-amber-900">
              <ShieldCheck className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <span>
                Safety Mode Active ({systemStatus.smsMode?.toUpperCase() || 'MOCK'}): Real SMS dispatch to players is simulated locally.
              </span>
            </div>
            <span className="text-[10px] bg-amber-200 text-amber-950 font-bold px-2 py-0.5 rounded">
              Local Guard
            </span>
          </div>
        )}

        {/* TAB NAVIGATION PILLS */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50/80 px-6 py-2.5">
          <div className="flex items-center gap-2 bg-gray-200/70 p-1 rounded-xl">
            <button
              type="button"
              onClick={() => setActiveTab('create')}
              className={`py-1.5 px-4 text-xs font-bold rounded-lg flex items-center gap-1.5 transition-all ${activeTab === 'create'
                ? 'bg-yellow-400 text-black shadow-sm'
                : 'text-gray-600 hover:text-black font-semibold'
                }`}
            >
              {editingSchedule ? (
                <>
                  <Pencil size={14} />
                  Edit Campaign #{editingSchedule.id}
                </>
              ) : (
                <>
                  <PlusCircle size={14} />
                  Schedule New Message
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('manage')}
              className={`py-1.5 px-4 text-xs font-bold rounded-lg flex items-center gap-1.5 transition-all ${activeTab === 'manage'
                ? 'bg-yellow-400 text-black shadow-sm'
                : 'text-gray-600 hover:text-black font-semibold'
                }`}
            >
              <ListOrdered size={14} />
              Scheduled Queue ({schedules.length})
            </button>
          </div>

          <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-600 font-medium bg-white px-2.5 py-1 rounded-lg border border-gray-200 shadow-sm">
            <Clock size={13} className="text-amber-600 animate-pulse" />
            <span>Current DateTime: <strong className="text-gray-900 font-mono font-bold">{currentCst.format('MMM D, YYYY, h:mm:ss A')} CST</strong></span>
          </div>
        </div>

        {/* TAB CONTENT */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50/40">

          {/* TAB 1: CREATE / EDIT SCHEDULE FORM */}
          {activeTab === 'create' ? (
            <form onSubmit={handleCreateSchedule} className="space-y-6 max-w-2xl mx-auto">

              {/* EDITING ALERT BANNER */}
              {editingSchedule && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between text-xs text-amber-900 shadow-sm animate-in fade-in">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-amber-500 text-white flex items-center justify-center font-bold">
                      <Pencil size={12} />
                    </div>
                    <span>
                      Editing Campaign: <strong>{editingSchedule.campaign_name || `Campaign #${editingSchedule.id}`}</strong>
                      {editingSchedule.status === 'cancelled' && ' (Currently Cancelled)'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-2.5 py-1 text-xs font-bold text-gray-700 bg-white hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors shadow-sm"
                  >
                    Cancel Edit (Create New)
                  </button>
                </div>
              )}

              {/* STEP 1: CAMPAIGN TITLE */}
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700">
                  Campaign Title / Internal Note (Optional)
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="e.g. Saturday 10K Classic Reminder"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <p className="text-[11px] text-gray-500">
                  This title is for your internal tracking and will not be seen by recipients.
                </p>
              </div>

              {/* STEP 2: AUDIENCE SELECTOR */}
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700">
                  Who Should Receive This? (Audience)
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <button
                    type="button"
                    onClick={() => setTargetType('all_active')}
                    className={`p-3 rounded-xl border text-left flex items-start gap-2.5 transition-all ${targetType === 'all_active'
                      ? 'border-yellow-400 bg-yellow-50/80 ring-2 ring-yellow-400/50 shadow-sm'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                  >
                    <div className={`p-2 rounded-lg ${targetType === 'all_active' ? 'bg-yellow-400 text-black shadow-sm' : 'bg-gray-100 text-gray-600'}`}>
                      <Users size={16} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">All Active Players</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                        Auto-evaluates active members live at send time
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTargetType('active_and_custom')}
                    className={`p-3 rounded-xl border text-left flex items-start gap-2.5 transition-all ${targetType === 'active_and_custom'
                      ? 'border-yellow-400 bg-yellow-50/80 ring-2 ring-yellow-400/50 shadow-sm'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                  >
                    <div className={`p-2 rounded-lg ${targetType === 'active_and_custom' ? 'bg-yellow-400 text-black shadow-sm' : 'bg-gray-100 text-gray-600'}`}>
                      <UserPlus size={16} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">Active + Extra People</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                        Active members PLUS added numbers (no duplicates)
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTargetType('custom')}
                    className={`p-3 rounded-xl border text-left flex items-start gap-2.5 transition-all ${targetType === 'custom'
                      ? 'border-yellow-400 bg-yellow-50/80 ring-2 ring-yellow-400/50 shadow-sm'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                  >
                    <div className={`p-2 rounded-lg ${targetType === 'custom' ? 'bg-yellow-400 text-black shadow-sm' : 'bg-gray-100 text-gray-600'}`}>
                      <ListOrdered size={16} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">
                        Specific People Only ({selectedRecipients.length})
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                        Fixed list of numbers or chosen directory players
                      </div>
                    </div>
                  </button>
                </div>

                {(targetType === 'all_active' || targetType === 'active_and_custom') && (
                  <div className="p-3.5 bg-gradient-to-br from-emerald-50/80 to-teal-50/40 rounded-xl border border-emerald-200/80 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-emerald-950">Active Club Players with Phone:</span>
                        <span className="font-bold text-xs bg-white text-emerald-800 px-2.5 py-0.5 rounded-full border border-emerald-200">
                          {activePlayers.length > 0 ? `${activePlayers.length} Active Players` : 'Loading...'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowActivePlayersList(!showActivePlayersList)}
                        className="text-xs font-semibold text-emerald-800 hover:text-emerald-950 bg-emerald-100 hover:bg-emerald-200 px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors"
                      >
                        {showActivePlayersList ? <EyeOff size={13} /> : <Eye size={13} />}
                        {showActivePlayersList ? 'Hide Active Players' : 'Show Active Players List'}
                      </button>
                    </div>

                    {showActivePlayersList && (
                      <div className="bg-white border border-emerald-200 rounded-xl p-3 shadow-sm space-y-2 animate-in fade-in duration-150">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                          <input
                            type="text"
                            value={activeSearchTerm}
                            onChange={(e) => setActiveSearchTerm(e.target.value)}
                            placeholder="Filter active players by name, # or phone..."
                            className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                          {activeSearchTerm && (
                            <button
                              type="button"
                              onClick={() => setActiveSearchTerm('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>

                        {activePlayersLoading ? (
                          <div className="py-4 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                            <Loader2 size={16} className="animate-spin text-emerald-600" />
                            <span>Loading active players...</span>
                          </div>
                        ) : filteredActivePlayers.length === 0 ? (
                          <div className="py-3 text-center text-xs text-gray-500">
                            {activeSearchTerm ? 'No active players match search' : 'No active players found'}
                          </div>
                        ) : (
                          <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-lg">
                            {filteredActivePlayers.map((player, idx) => (
                              <div
                                key={player.player_id || player.phone || idx}
                                className="px-3 py-2 flex items-center justify-between text-xs hover:bg-gray-50/80 transition-colors"
                              >
                                <div>
                                  <div className="font-semibold text-gray-900 flex items-center gap-1.5">
                                    <span>{player.player_name || 'Active Member'}</span>
                                    {player.player_number && (
                                      <span className="text-[11px] font-mono text-gray-500 bg-gray-100 px-1.5 py-0.2 rounded">
                                        #{player.player_number}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px] font-mono text-gray-500 mt-0.5">
                                    {player.phone}
                                  </div>
                                </div>
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  Active
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {(targetType === 'custom' || targetType === 'active_and_custom') && (
                  <div className="p-3.5 bg-yellow-50/60 border border-yellow-200 rounded-xl space-y-3 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-indigo-950 flex items-center gap-1.5">
                        <Users size={14} className="text-indigo-600" />
                        Selected Players: <strong className="text-indigo-700">{selectedRecipients.length}</strong>
                      </span>

                      {onOpenMessaging && (
                        <button
                          type="button"
                          onClick={onOpenMessaging}
                          className="text-xs text-indigo-700 hover:text-indigo-900 font-semibold underline"
                        >
                          {selectedRecipients.length > 0 ? 'Edit / Select More Players' : '+ Select Players from Directory'}
                        </button>
                      )}
                    </div>

                    {selectedRecipients.length > 0 && (
                      <div className="max-h-24 overflow-y-auto flex flex-wrap gap-1 p-2 bg-white rounded-lg border border-indigo-100">
                        {selectedRecipients.slice(0, 10).map((r, i) => (
                          <span key={i} className="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded font-medium text-[11px]">
                            {r.player_name || r.phone || `Player ${r.player_number}`}
                          </span>
                        ))}
                        {selectedRecipients.length > 10 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[11px]">
                            +{selectedRecipients.length - 10} more
                          </span>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Add Direct Phone Numbers (comma/space separated):
                      </label>
                      <input
                        type="text"
                        value={manualPhoneInput}
                        onChange={(e) => setManualPhoneInput(e.target.value)}
                        placeholder="e.g. 9565551234, 9565555678"
                        className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* STEP 3: SCHEDULE DATE & TIME (WITH QUICK PRESETS) */}
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3.5">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700">
                    When Should It Send? (Central Time CST/CDT)
                  </label>
                  <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
                    America/Chicago Timezone
                  </span>
                </div>

                {/* Quick Presets Section */}
                <div className="space-y-2 bg-gray-50/80 p-3 rounded-xl border border-gray-200/80">
                  {/* Row 1: Recurring Daily Presets */}
                  <div>
                    <span className="text-[11px] font-bold text-purple-700 mb-1.5 flex items-center gap-1">
                      <Repeat size={12} className="text-purple-600" />
                      Recurring Daily Presets:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleApplyPreset('everyday_10am')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all flex items-center gap-1.5 ${isRecurring && scheduleTime === '10:00'
                          ? 'bg-black text-yellow-400 border-black shadow-sm font-bold'
                          : 'bg-white hover:bg-yellow-50 text-gray-900 border-gray-300 font-semibold'
                          }`}
                      >
                        <Repeat size={12} />
                        Everyday 10:00 AM
                      </button>
                      <button
                        type="button"
                        onClick={() => handleApplyPreset('everyday_3pm')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all flex items-center gap-1.5 ${isRecurring && scheduleTime === '15:00'
                          ? 'bg-black text-yellow-400 border-black shadow-sm font-bold'
                          : 'bg-white hover:bg-yellow-50 text-gray-900 border-gray-300 font-semibold'
                          }`}
                      >
                        <Repeat size={12} />
                        Everyday 3:00 PM
                      </button>
                      <button
                        type="button"
                        onClick={() => handleApplyPreset('everyday_7pm')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all flex items-center gap-1.5 ${isRecurring && scheduleTime === '19:00'
                          ? 'bg-black text-yellow-400 border-black shadow-sm font-bold'
                          : 'bg-white hover:bg-yellow-50 text-gray-900 border-gray-300 font-semibold'
                          }`}
                      >
                        <Repeat size={12} />
                        Everyday 7:00 PM
                      </button>
                    </div>
                  </div>

                  {/* Row 2: One-Time Quick Presets */}
                  <div className="pt-1 border-t border-gray-200/60">
                    <span className="text-[11px] font-semibold text-gray-500 mb-1.5 flex items-center gap-1">
                      <Zap size={12} className="text-amber-500" />
                      One-Time Quick Presets:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleApplyPreset('next_10am')}
                        className="px-2.5 py-1 text-xs font-medium rounded-lg bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 transition-colors"
                      >
                        Today / Next 10:00 AM
                      </button>
                      <button
                        type="button"
                        onClick={() => handleApplyPreset('next_3pm')}
                        className="px-2.5 py-1 text-xs font-medium rounded-lg bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 transition-colors"
                      >
                        Today / Next 3:00 PM
                      </button>
                      <button
                        type="button"
                        onClick={() => handleApplyPreset('tonight_6pm')}
                        className="px-2.5 py-1 text-xs font-medium rounded-lg bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 transition-colors"
                      >
                        Tonight 6:00 PM
                      </button>
                      <button
                        type="button"
                        onClick={() => handleApplyPreset('in_1_hour')}
                        className="px-2.5 py-1 text-xs font-medium rounded-lg bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 transition-colors"
                      >
                        In 1 Hour
                      </button>
                    </div>
                  </div>
                </div>

                {/* Repeat Frequency Selector */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <Repeat size={13} className="text-purple-600" />
                      Repeat Frequency:
                    </span>
                    {isRecurring && (
                      <span className="text-[11px] font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
                        🔄 Repeating Active
                      </span>
                    )}
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { id: 'once', label: 'Run Once', desc: 'Single broadcast' },
                      { id: 'daily', label: 'Every day', desc: 'Repeats daily at this time' },
                      { id: 'weekdays', label: 'Weekdays', desc: 'Mon - Fri only' },
                      { id: 'weekends', label: 'Weekends', desc: 'Sat - Sun only' },
                    ].map((f) => {
                      const isSelected = repeatFrequency === f.id;
                      return (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => {
                            setRepeatFrequency(f.id);
                            setIsRecurring(f.id !== 'once');
                          }}
                          className={`p-2 rounded-xl text-left border transition-all ${isSelected
                            ? 'border-purple-500 bg-purple-50/80 ring-1 ring-purple-500 shadow-sm'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                        >
                          <div className="flex items-center justify-between mb-0.5">
                            <span className={`text-xs font-bold ${isSelected ? 'text-purple-900' : 'text-gray-800'}`}>
                              {f.label}
                            </span>
                            {isSelected && <CheckCircle2 size={12} className="text-purple-600" />}
                          </div>
                          <span className="text-[10px] text-gray-500 block leading-tight">{f.desc}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Pickers */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                      <Calendar size={13} className="text-blue-600" />
                      {isRecurring ? 'First Run Date (CST)' : 'Date (CST)'}
                    </label>
                    <input
                      type="date"
                      value={scheduleDate}
                      min={moment().tz(CST_TIMEZONE).format('YYYY-MM-DD')}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                      <Clock size={13} className="text-blue-600" />
                      Send Time (CST - America/Chicago)
                    </label>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                </div>

                {/* Natural Language Confirmation / Warning */}
                {computedSchedule && (
                  <div
                    className={`p-3 rounded-xl border text-xs flex items-center gap-2.5 ${computedSchedule.isPast
                      ? 'bg-rose-50 border-rose-200 text-rose-800'
                      : isRecurring
                        ? 'bg-purple-50 border-purple-200 text-purple-900'
                        : 'bg-blue-50/80 border-blue-200 text-blue-900'
                      }`}
                  >
                    {computedSchedule.isPast ? (
                      <>
                        <AlertTriangle size={16} className="text-rose-600 flex-shrink-0" />
                        <span>
                          <strong>Selected time is in the past!</strong> Please choose a future time (currently {currentCst.format('h:mm A')} CST).
                        </span>
                      </>
                    ) : isRecurring ? (
                      <>
                        <Repeat size={16} className="text-purple-600 flex-shrink-0" />
                        <span>
                          <strong>🔁 Recurring Campaign:</strong> Dispatches automatically{' '}
                          <strong>
                            {repeatFrequency === 'daily'
                              ? 'every day'
                              : repeatFrequency === 'weekdays'
                                ? 'every weekday (Mon-Fri)'
                                : 'every weekend (Sat-Sun)'}{' '}
                            at {computedSchedule.timeOnly} CST
                          </strong>
                          . First run scheduled for: <strong>{computedSchedule.formatted} ({computedSchedule.fromNowText})</strong>.
                        </span>
                      </>
                    ) : (
                      <>
                        <CalendarCheck size={16} className="text-blue-600 flex-shrink-0" />
                        <span>
                          Will trigger once on <strong>{computedSchedule.formatted} CST</strong> ({computedSchedule.fromNowText}).
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* STEP 4: MESSAGE CONTENT & PREVIEW */}
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700">
                    Message Content
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPresetsModal(true)}
                      className="px-2.5 py-1 text-xs font-bold text-gray-800 bg-amber-50 hover:bg-amber-100 rounded-lg border border-amber-300 flex items-center gap-1.5 transition-all shadow-sm"
                      title="Browse & insert pre-written SMS templates or your saved drafts"
                    >
                      <Library size={12} className="text-amber-600" />
                      <span>Presets Library</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowOptimizerModal(true)}
                      className="px-2.5 py-1 text-xs font-bold text-gray-900 bg-yellow-400 hover:bg-yellow-500 rounded-lg border border-yellow-500 flex items-center gap-1.5 transition-all shadow-sm"
                      title="Rewrite & optimize SMS with Claude AI (160 chars & policy check)"
                    >
                      <Sparkles size={12} className="text-gray-950" />
                      <span>AI Optimize</span>
                    </button>
                    <span className="text-xs text-gray-500 font-mono">
                      {charCount} chars • {segments} segment(s)
                    </span>
                  </div>
                </div>

                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  required
                  placeholder="Type the message to be scheduled..."
                  className="w-full p-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none leading-relaxed"
                />

                {/* Recipient Phone Preview */}
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-200/80">
                  <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Smartphone size={13} className="text-gray-400" />
                    Recipient SMS Preview
                  </div>
                  <div className="bg-gray-950 text-white text-xs p-3.5 rounded-2xl rounded-br-sm shadow-md whitespace-pre-wrap leading-relaxed max-w-sm border border-gray-800">
                    {message.trim() ? message : <span className="opacity-60 italic">Preview will appear here...</span>}
                  </div>
                </div>
              </div>

              {/* ACTION FOOTER */}
              <div className="flex items-center justify-end gap-3 pt-2">
                {editingSchedule ? (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    Cancel Edit
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setActiveTab('manage')}
                    className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    View Queue ({schedules.length})
                  </button>
                )}

                <button
                  type="submit"
                  disabled={submitting || (computedSchedule && computedSchedule.isPast)}
                  className={`px-6 py-2.5 rounded-xl text-sm font-bold shadow-md hover:shadow-lg flex items-center gap-2 transition-all disabled:opacity-50 ${editingSchedule
                    ? 'bg-amber-500 hover:bg-amber-600 text-gray-950 font-bold'
                    : 'bg-amber-400 hover:bg-amber-500 text-gray-950 font-bold'
                    }`}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {editingSchedule ? 'Saving Changes...' : 'Creating Schedule...'}
                    </>
                  ) : (
                    <>
                      {editingSchedule ? <Pencil size={16} /> : <CalendarCheck size={16} />}
                      {editingSchedule ? 'Save Campaign Changes' : 'Confirm & Schedule Message'}
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (

            /* TAB 2: MANAGE SCHEDULES QUEUE */
            <div className="space-y-4">
              {/* Filter and Actions Bar */}
              <div className="flex items-center justify-between flex-wrap gap-2 pb-1">
                <div className="flex items-center gap-1 bg-gray-200/70 p-1 rounded-xl">
                  {['all', 'pending', 'completed', 'cancelled', 'failed'].map((st) => (
                    <button
                      key={st}
                      type="button"
                      onClick={() => setStatusFilter(st)}
                      className={`px-3 py-1 text-xs font-bold rounded-lg capitalize transition-all ${statusFilter === st
                        ? 'bg-black text-white font-bold shadow-sm'
                        : 'text-gray-600 hover:text-black font-semibold'
                        }`}
                    >
                      {st}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={fetchSchedules}
                    className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200/80 rounded-lg transition-colors"
                    title="Refresh list"
                  >
                    <RotateCcw size={16} className={loading ? 'animate-spin' : ''} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('create')}
                    className="px-3.5 py-1.5 bg-amber-400 hover:bg-amber-500 text-black text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-sm"
                  >
                    <PlusCircle size={14} />
                    New Schedule
                  </button>
                </div>
              </div>

              {/* Schedules Table */}
              {loading ? (
                <div className="py-20 text-center text-gray-400">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-600" />
                  Loading scheduled messages...
                </div>
              ) : schedules.length === 0 ? (
                <div className="py-16 text-center bg-white rounded-2xl border border-dashed border-gray-300 p-8 space-y-3">
                  <div className="p-3 bg-blue-50 text-blue-600 rounded-full w-12 h-12 mx-auto flex items-center justify-center">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">No scheduled messages found</h4>
                    <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
                      Plan automated announcements or promotions in advance so they send at the perfect moment.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab('create')}
                    className="px-4 py-2 bg-amber-400 hover:bg-amber-500 text-gray-950 text-xs font-bold rounded-xl shadow-sm inline-flex items-center gap-1.5"
                  >
                    <PlusCircle size={14} />
                    Schedule First Message
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-2xl shadow-sm bg-white">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 text-left">Campaign / Message</th>
                        <th className="px-4 py-3 text-left">Target Audience</th>
                        <th className="px-4 py-3 text-left">Send Time (CST)</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Delivery Results</th>
                        <th className="px-3 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {schedules.map((row) => {
                        const isActionLoading = actionLoadingId === row.id;
                        const rowMoment = moment(row.scheduled_time).tz(CST_TIMEZONE);

                        return (
                          <tr key={row.id} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-4 py-3 max-w-xs">
                              {row.campaign_name && (
                                <div className="font-bold text-gray-900 text-xs mb-0.5">
                                  {row.campaign_name}
                                </div>
                              )}
                              {row.is_recurring && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200 mb-1">
                                  <Repeat size={10} />
                                  {row.cron_expression === 'weekdays' ? 'Every Weekday' : row.cron_expression === 'weekends' ? 'Every Weekend' : 'Every Day'}
                                </span>
                              )}
                              <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                                {row.message}
                              </p>
                              <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mt-1.5 pt-1 border-t border-gray-100">
                                <User size={12} className="text-amber-600 flex-shrink-0" />
                                <span>Created by: <span className="font-semibold text-gray-800">{row.created_by_email || row.created_by_userid || 'Staff'}</span></span>
                              </div>
                            </td>

                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-0.5 rounded-full border shadow-sm ${
                                row.target_type === 'active_and_custom'
                                  ? 'bg-yellow-100 text-yellow-900 border-yellow-300'
                                  : row.target_type === 'all_active'
                                  ? 'bg-gray-100 text-gray-800 border-gray-200'
                                  : 'bg-white text-gray-800 border-gray-300'
                              }`}>
                                <Users size={11} className={row.target_type === 'active_and_custom' ? 'text-yellow-700' : 'text-gray-500'} />
                                {row.target_type === 'all_active'
                                  ? 'All Active'
                                  : row.target_type === 'active_and_custom'
                                  ? `Active + Custom (${row.total_count || (row.recipients_snapshot?.length || 0)})`
                                  : `Custom (${row.total_count || (row.recipients_snapshot?.length || 0)})`}
                              </span>
                            </td>

                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-700">
                              <div className="font-bold text-gray-900">
                                {rowMoment.format('MMM D, YYYY')}
                              </div>
                              <div className="text-gray-500 font-mono text-[11px]">
                                {rowMoment.format('h:mm A')} CST
                              </div>
                              {row.is_recurring && (
                                <span className="text-[10px] text-purple-700 font-semibold block">
                                  {row.status === 'cancelled' ? 'Recurrence stopped' : row.status === 'processing' ? "Dispatching today's run" : 'Auto-reschedules daily'}
                                </span>
                              )}
                            </td>

                            <td className="px-4 py-3 whitespace-nowrap">
                              {getStatusBadge(row.status, row.is_recurring)}
                            </td>

                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                              {row.status === 'completed' ? (
                                <div className="font-medium">
                                  {row.simulated_count > 0 ? (
                                    <span className="text-amber-700">Simulated: {row.simulated_count}</span>
                                  ) : (
                                    <span className="text-emerald-700 font-bold">Sent: {row.sent_count}</span>
                                  )}
                                  {row.skipped_count > 0 && (
                                    <span className="text-gray-500 ml-1.5">• Skip: {row.skipped_count}</span>
                                  )}
                                  {row.failed_count > 0 && (
                                    <span className="text-rose-600 ml-1.5 font-bold">• Fail: {row.failed_count}</span>
                                  )}
                                </div>
                              ) : row.last_error ? (
                                <span className="text-rose-600 text-xs line-clamp-1" title={row.last_error}>
                                  {row.last_error}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>

                            <td className="px-3 py-3 whitespace-nowrap text-center">
                              <div className="flex flex-col items-center justify-center gap-1.5">
                                {/* Edit Button - editable whenever not currently sending */}
                                {row.status !== 'processing' && (
                                  <button
                                    type="button"
                                    disabled={isActionLoading}
                                    onClick={() => handleStartEdit(row)}
                                    className="w-7 h-7 flex items-center justify-center text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-lg border border-amber-200 transition-colors disabled:opacity-50 shadow-xs"
                                    title="Edit this scheduled campaign"
                                    aria-label="Edit"
                                  >
                                    <Pencil size={13} />
                                  </button>
                                )}

                                {/* Test Run - only for pending */}
                                {row.status === 'pending' && (
                                  <button
                                    type="button"
                                    disabled={isActionLoading}
                                    onClick={() => handleTestRunSchedule(row)}
                                    className="w-7 h-7 flex items-center justify-center text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors disabled:opacity-50 shadow-xs"
                                    title="Run immediately in safe test mode"
                                    aria-label="Test Run"
                                  >
                                    {isActionLoading ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <Play size={13} />
                                    )}
                                  </button>
                                )}

                                {/* Cancel - only for pending */}
                                {row.status === 'pending' && (
                                  <button
                                    type="button"
                                    disabled={isActionLoading}
                                    onClick={() => handleCancelSchedule(row)}
                                    className="w-7 h-7 flex items-center justify-center text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 transition-colors disabled:opacity-50 shadow-xs"
                                    title="Cancel this pending schedule"
                                    aria-label="Cancel"
                                  >
                                    <XCircle size={13} />
                                  </button>
                                )}

                                {/* Delete Button - available for any schedule not actively processing */}
                                {row.status !== 'processing' && (
                                  <button
                                    type="button"
                                    disabled={isActionLoading}
                                    onClick={() => handleDeleteSchedule(row)}
                                    className="w-7 h-7 flex items-center justify-center text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-lg border border-rose-200 transition-colors disabled:opacity-50 shadow-xs"
                                    title="Permanently delete this scheduled campaign"
                                    aria-label="Delete"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* MODAL FOOTER */}
        <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <Info size={13} className="text-gray-400" />
            <span>Timezone: America/Chicago (Central Standard Time)</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 font-semibold rounded-xl transition-colors shadow-sm"
          >
            Close
          </button>
        </div>
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {deleteConfirmTarget && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-rose-100 animate-in zoom-in-95 duration-150">
            {/* Header */}
            <div className="px-6 py-4 bg-rose-50/80 border-b border-rose-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
                  <Trash2 size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Delete Scheduled Campaign</h3>
                  <p className="text-xs text-rose-600 font-medium">This action cannot be undone</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDeleteConfirmTarget(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 text-xs">
              <p className="text-gray-600 leading-relaxed text-sm">
                Are you sure you want to permanently delete this scheduled campaign?
              </p>

              <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
                <div className="flex justify-between items-start gap-2">
                  <span className="text-gray-500 font-medium">Campaign:</span>
                  <span className="font-bold text-gray-900 text-right">
                    {deleteConfirmTarget.campaign_name || 'Untitled Campaign'}
                  </span>
                </div>
                <div className="flex justify-between items-center gap-2">
                  <span className="text-gray-500 font-medium">Audience:</span>
                  <span className="font-semibold text-gray-800">
                    {deleteConfirmTarget.target_type === 'all_active'
                      ? 'All Active Players'
                      : `Custom (${deleteConfirmTarget.total_count || '?'} players)`}
                  </span>
                </div>
                {deleteConfirmTarget.scheduled_time && (
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-gray-500 font-medium">Scheduled for:</span>
                    <span className="font-mono text-gray-700">
                      {moment(deleteConfirmTarget.scheduled_time).tz(CST_TIMEZONE).format('MMM D, YYYY h:mm A')} CST
                    </span>
                  </div>
                )}
                {deleteConfirmTarget.message && (
                  <div className="pt-2 border-t border-gray-200/80">
                    <span className="text-gray-500 font-medium block mb-1">Message Preview:</span>
                    <div className="p-2 bg-white rounded border border-gray-200 text-gray-700 line-clamp-2 italic">
                      "{deleteConfirmTarget.message}"
                    </div>
                  </div>
                )}
              </div>

              <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-amber-900 text-[11px] flex items-start gap-2">
                <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <span>
                  The scheduled job will be stopped and permanently removed from the dispatch queue. No SMS will be sent.
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-200 flex justify-end gap-2.5">
              <button
                type="button"
                disabled={actionLoadingId === deleteConfirmTarget.id}
                onClick={() => setDeleteConfirmTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 bg-white hover:bg-gray-100 transition-colors shadow-sm disabled:opacity-50"
              >
                No, Keep Campaign
              </button>
              <button
                type="button"
                disabled={actionLoadingId === deleteConfirmTarget.id}
                onClick={() => confirmDeleteSchedule(deleteConfirmTarget.id)}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 transition-colors shadow-sm disabled:opacity-50"
              >
                {actionLoadingId === deleteConfirmTarget.id ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={13} />
                    <span>Yes, Delete Campaign</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL CONFIRMATION MODAL */}
      {cancelConfirmTarget && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-200 animate-in zoom-in-95 duration-150">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Cancel Scheduled SMS</h3>
                  <p className="text-xs text-gray-500">Stop this campaign from sending</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCancelConfirmTarget(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-3 text-xs">
              <p className="text-gray-600 leading-relaxed text-sm">
                Are you sure you want to cancel scheduled campaign <strong>"{cancelConfirmTarget.campaign_name || 'Untitled'}"</strong>?
              </p>
              <p className="text-gray-500">
                Its status will be changed to cancelled. You can edit or re-schedule it later from the management table.
              </p>
            </div>
            <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-200 flex justify-end gap-2.5">
              <button
                type="button"
                disabled={actionLoadingId === cancelConfirmTarget.id}
                onClick={() => setCancelConfirmTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 bg-white hover:bg-gray-100 transition-colors shadow-sm disabled:opacity-50"
              >
                Keep Active
              </button>
              <button
                type="button"
                disabled={actionLoadingId === cancelConfirmTarget.id}
                onClick={() => confirmCancelSchedule(cancelConfirmTarget.id)}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 transition-colors shadow-sm disabled:opacity-50"
              >
                {actionLoadingId === cancelConfirmTarget.id ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    <span>Cancelling...</span>
                  </>
                ) : (
                  <span>Yes, Cancel Campaign</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TEST RUN CONFIRMATION MODAL */}
      {testRunConfirmTarget && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-blue-100 animate-in zoom-in-95 duration-150">
            <div className="px-6 py-4 bg-blue-50/80 border-b border-blue-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                  <Play size={18} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Run Safe Test Mode</h3>
                  <p className="text-xs text-blue-700">Simulate campaign dispatch immediately</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setTestRunConfirmTarget(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-3 text-xs">
              <p className="text-gray-600 leading-relaxed text-sm">
                Run <strong>"{testRunConfirmTarget.campaign_name || 'Untitled'}"</strong> immediately in safe test mode?
              </p>
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-200 text-blue-900 text-xs">
                In test mode, messages are simulated cleanly and logged to the SMS records. Actual players will not receive text messages.
              </div>
            </div>
            <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-200 flex justify-end gap-2.5">
              <button
                type="button"
                disabled={actionLoadingId === testRunConfirmTarget.id}
                onClick={() => setTestRunConfirmTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 bg-white hover:bg-gray-100 transition-colors shadow-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionLoadingId === testRunConfirmTarget.id}
                onClick={() => confirmTestRunSchedule(testRunConfirmTarget.id)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 transition-colors shadow-sm disabled:opacity-50"
              >
                {actionLoadingId === testRunConfirmTarget.id ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    <span>Running Test...</span>
                  </>
                ) : (
                  <>
                    <Play size={13} />
                    <span>Confirm Test Run</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI MESSAGE OPTIMIZER MODAL */}
      <SmsMessageOptimizerModal
        isOpen={showOptimizerModal}
        onClose={() => setShowOptimizerModal(false)}
        initialMessage={message}
        onApplyMessage={(optMsg) => setMessage(optMsg)}
        recipientContext={
          targetType === 'all_active'
            ? 'All active Elite Poker Lounge club members in McAllen, Texas'
            : `Scheduled campaign for ${targetType} audience`
        }
      />

      {/* SMS PRESETS LIBRARY MODAL */}
      <SmsPresetsLibraryModal
        isOpen={showPresetsModal}
        onClose={() => setShowPresetsModal(false)}
        onSelectPreset={(presetBody) => setMessage(presetBody)}
        onOpenOptimizer={(presetBody) => {
          setMessage(presetBody);
          setShowOptimizerModal(true);
        }}
        currentDraftMessage={message}
      />
    </div>
  );
}
